<template>
  <div class="flex flex-col w-full h-full pt-8 pb-12 px-4 sm:px-0 bg-white items-center text-center">

    <div class="w-16 h-16 bg-pink-100 text-pink-600 rounded-full flex items-center justify-center mb-6 shadow-inner">
      <i class="pi pi-check-circle text-2xl"></i>
    </div>

    <h2 class="text-2xl sm:text-3xl font-black text-slate-900 mb-2 leading-tight">כמעט סיימנו!</h2>
    <h3 class="text-xl font-bold text-slate-700 mb-6">אימות קצר וזהו</h3>

    <div class="w-full flex flex-col justify-center items-center mt-4 bg-slate-50 rounded-md p-8 sm:p-10 border border-slate-100 shadow-sm max-w-sm">
      <p class="text-slate-600 text-sm mb-8 leading-relaxed">
        שלחנו קוד אימות ב-SMS למספר הטלפון שהזנתם. <br/> אנא הזינו אותו כאן למטה.
      </p>

      <div style="direction: ltr;" class="mb-10 w-full flex justify-center">
        <InputOtp
          v-model="otpCode"
          integerOnly
          :pt="{
            root: { class: 'flex justify-center w-full gap-2 sm:gap-3' },
            input: { class: 'w-12 h-14 text-xl sm:w-16 sm:h-16 sm:text-2xl font-black text-center !bg-white !border-slate-200 !text-slate-900 rounded-xl focus:!border-pink-500 focus:!ring-pink-500 transition-colors shadow-sm' }
          }"
        />
      </div>

      <Button
        v-wave
        class="w-full !bg-gradient-to-r !from-pink-500 !to-purple-600 hover:!from-pink-400 hover:!to-purple-500 !text-white !font-bold !py-4 !rounded-xl !border-0 transition-all !shadow-lg shadow-pink-500/30 transform hover:-translate-y-1 flex items-center justify-center text-lg mt-auto disabled:opacity-50 disabled:hover:-translate-y-0 disabled:shadow-none disabled:cursor-not-allowed"
        :disabled="otpCode.length !== 4"
        label="סיום הרשמה והתחברות"
        :loading="loading"
        @click="createBusiness"
      />

      <div class="mt-6 text-sm text-slate-500">
        <span>לא קיבלת את הקוד? </span>
        <button
          type="button"
          class="font-bold underline text-pink-600 hover:text-pink-500 disabled:no-underline disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
          :disabled="cooldown > 0 || resending"
          @click="resendCode"
        >
          {{ resendLabel }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {useToast} from "vue-toastification";
import {ref, computed, onMounted, onUnmounted} from "vue";
import {useRouter} from "vue-router";
import InputOtp from 'primevue/inputotp';
import Button from 'primevue/button';
import {AdminApi} from "@app/api/admin/admin-api";
import {AdminRouteNames, AuthRouteNames} from "@app/router/route-names";
import {event} from "vue-gtag";
import { trackEvent } from '@app/composables/posthog';

const toast = useToast();
const router = useRouter();

const loading = ref(false);
const otpCode = ref<string>('');

const RESEND_COOLDOWN = 30;
const cooldown = ref(RESEND_COOLDOWN);
const resending = ref(false);
let cooldownTimer: ReturnType<typeof setInterval> | undefined;

const resendLabel = computed(() =>
  cooldown.value > 0 ? `שליחה חוזרת בעוד ${cooldown.value} שניות` : 'שליחה חוזרת של הקוד',
);

const startCooldown = () => {
  cooldown.value = RESEND_COOLDOWN;
  if (cooldownTimer) clearInterval(cooldownTimer);
  cooldownTimer = setInterval(() => {
    if (cooldown.value > 0) {
      cooldown.value--;
    } else if (cooldownTimer) {
      clearInterval(cooldownTimer);
      cooldownTimer = undefined;
    }
  }, 1000);
};

const resendCode = async () => {
  if (cooldown.value > 0 || resending.value) return;
  resending.value = true;
  try {
    await AdminApi.resendVerificationCode();
    trackEvent('registration_otp_resent');
    toast.success('שלחנו לך קוד אימות חדש ב-SMS');
    startCooldown();
  } catch (e) {
    toast.error('לא הצלחנו לשלוח קוד חדש, נסו שוב בעוד רגע');
  } finally {
    resending.value = false;
  }
};

onMounted(startCooldown);
onUnmounted(() => {
  if (cooldownTimer) clearInterval(cooldownTimer);
});

const createBusiness = async () => {
  if (otpCode.value.length !== 4)
    return;

  loading.value = true;

  const metaEventId = `${Date.now()}_StartTrial`;

  try {
    try {
      window.fbq?.('track', 'StartTrial', {value: '0.00', currency: 'ILS', predicted_ltv: '0.00'}, { eventID: metaEventId });
    } catch (e) {
      console.log(`Failed to send report, ${e}`)
    }

    try {
      window.oaiq?.('measure', 'trial_started', { type: 'plan_enrollment' });
    } catch (e) {
      console.log(`Failed to send OpenAI report, ${e}`)
    }

    try {
      event('generate_lead');
      event('conversion', {
        send_to: 'AW-16622960794/KDD6CLXh6dQZEJqBufY9',
        value: 1.0,
        currency: 'ILS'
      });
    } catch (e) {
      console.log(`Failed to send event, ${e}`)
    }

    await AdminApi.validateBusiness({verificationCode: otpCode.value, metaEventId});
    trackEvent('business_verified');

    // Pass onboarding context to AI setup
    let query: Record<string, string> = {};
    try {
      const ctx = sessionStorage.getItem('onboarding-context');
      if (ctx) {
        query = JSON.parse(ctx);
        sessionStorage.removeItem('onboarding-context');
      }
    } catch { /* ignore */ }

    await router.replace({ name: AuthRouteNames.AiSetup, query });
  } catch (e) {
    toast.error('הקוד שהוזן שגוי, אנא נסו שוב')
  }
  loading.value = false;

};
</script>
